import type { BridgeData, RampData, SurfaceData, TornadoData, TrackData, Vec2, WaterwayData, WorldObstacleData } from "../types";
import { add, clip, cloneVec, cross, dot, length, scale, sub, unit } from "./math";
import { circleObstacleContact, raycastObstacle, type WorldContact } from "./obstacles";

export interface TrackProjection {
  point: Vec2;
  distanceFromCenter: number;
  progress: number;
  segmentIndex: number;
  segmentFraction: number;
  signedLateralOffset: number;
  allowedHalfWidth: number;
}

export class TrackRuntime {
  readonly data: TrackData;
  readonly centerline: Vec2[];
  readonly leftBoundary: Vec2[];
  readonly rightBoundary: Vec2[];
  readonly length: number;
  readonly obstacles: WorldObstacleData[];
  readonly ramps: RampData[];
  readonly tornadoes: TornadoData[];
  readonly bridges: BridgeData[];
  readonly surfaces: SurfaceData[];
  readonly waterways: WaterwayData[];
  readonly openJunctions: Array<[number, number, number]>;
  private readonly segments: Array<[Vec2, Vec2]>;
  private readonly segmentLengths: number[];
  private readonly cumulative: number[];

  constructor(data: TrackData) {
    this.data = data;
    this.centerline = data.centerline.map(cloneVec);
    this.segments = this.centerline.map((point, index) => [point, this.centerline[(index + 1) % this.centerline.length] as Vec2]);
    this.segmentLengths = this.segments.map(([start, end]) => length(sub(end, start)));
    this.cumulative = [0];
    for (const segmentLength of this.segmentLengths) {
      this.cumulative.push((this.cumulative.at(-1) ?? 0) + segmentLength);
    }
    this.length = this.cumulative.at(-1) ?? 0;
    this.obstacles = (data.obstacles ?? []).map((item) => ({ ...item, center: cloneVec(item.center) }));
    this.ramps = (data.ramps ?? []).map((item) => ({ ...item }));
    this.tornadoes = (data.tornadoes ?? []).map((item) => ({ ...item }));
    this.bridges = (data.bridges ?? []).map((item) => ({ ...item }));
    this.surfaces = (data.surfaces ?? []).map((item) => ({ ...item }));
    this.waterways = (data.waterways ?? []).map((item) => ({
      ...item,
      centerline: item.centerline.map(cloneVec),
    }));
    this.openJunctions = (data.open_junctions ?? []).map((item) => [...item] as [number, number, number]);
    [this.leftBoundary, this.rightBoundary] = this.makeBoundaries();
  }

  private makeBoundaries(): [Vec2[], Vec2[]] {
    const left: Vec2[] = [];
    const right: Vec2[] = [];
    const count = this.centerline.length;
    for (let index = 0; index < count; index += 1) {
      const point = this.centerline[index] as Vec2;
      const tangent = unit(sub(this.centerline[(index + 1) % count] as Vec2, this.centerline[(index - 1 + count) % count] as Vec2));
      const normal: Vec2 = [-tangent[1], tangent[0]];
      left.push(add(point, scale(normal, this.data.width_left[index] ?? 0)));
      right.push(sub(point, scale(normal, this.data.width_right[index] ?? 0)));
    }
    return [left, right];
  }

  project(point: Vec2): TrackProjection {
    return this.projectIndices(point, this.segments.map((_, index) => index));
  }

  projectNear(point: Vec2, segmentIndex: number, window = 2): TrackProjection {
    const count = this.segments.length;
    const indices = [...new Set(Array.from({ length: window * 2 + 1 }, (_, offset) => (segmentIndex + offset - window + count) % count))].sort((a, b) => a - b);
    return this.projectIndices(point, indices);
  }

  private projectIndices(point: Vec2, indices: number[]): TrackProjection {
    let best: TrackProjection | null = null;
    for (const index of indices) {
      const [start, end] = this.segments[index] as [Vec2, Vec2];
      const vector = sub(end, start);
      const squared = dot(vector, vector);
      const fraction = squared <= 1e-12 ? 0 : clip(dot(sub(point, start), vector) / squared, 0, 1);
      const candidate = add(start, scale(vector, fraction));
      const distance = length(sub(candidate, point));
      if (best && distance >= best.distanceFromCenter) continue;
      const tangent = unit(vector);
      const lateral = sub(point, candidate);
      const signedLateral = tangent[0] * lateral[1] - tangent[1] * lateral[0];
      const nextIndex = (index + 1) % this.centerline.length;
      const widths = signedLateral >= 0 ? this.data.width_left : this.data.width_right;
      const allowed = (widths[index] ?? 0) + fraction * ((widths[nextIndex] ?? 0) - (widths[index] ?? 0));
      best = {
        point: candidate,
        distanceFromCenter: distance,
        progress: (this.cumulative[index] ?? 0) + fraction * (this.segmentLengths[index] ?? 0),
        segmentIndex: index,
        segmentFraction: fraction,
        signedLateralOffset: signedLateral,
        allowedHalfWidth: allowed,
      };
    }
    if (!best) throw new Error("Track projection requires at least one segment.");
    return best;
  }

  contains(point: Vec2, margin = 0): boolean {
    const projection = this.project(point);
    return Math.abs(projection.signedLateralOffset) <= projection.allowedHalfWidth - margin;
  }

  raycast(origin: Vec2, direction: Vec2, maxRange: number): number {
    return this.raycastWorld(origin, direction, maxRange).distance;
  }

  raycastWorld(origin: Vec2, direction: Vec2, maxRange: number): { distance: number; objectId: string } {
    let nearest = maxRange;
    let objectId = "";
    for (const boundary of [this.leftBoundary, this.rightBoundary]) {
      for (let index = 0; index < boundary.length; index += 1) {
        const start = boundary[index] as Vec2;
        const end = boundary[(index + 1) % boundary.length] as Vec2;
        if (this.segmentInsideOpenJunction(start, end)) continue;
        const segment = sub(end, start);
        const denominator = cross(direction, segment);
        if (Math.abs(denominator) < 1e-10) continue;
        const offset = sub(start, origin);
        const rayT = cross(offset, segment) / denominator;
        const segmentT = cross(offset, direction) / denominator;
        if (rayT >= 0 && segmentT >= 0 && segmentT <= 1) nearest = Math.min(nearest, rayT);
      }
    }
    for (const obstacle of this.obstacles) {
      const candidate = raycastObstacle(obstacle, origin, direction, maxRange);
      if (candidate < nearest) {
        nearest = candidate;
        objectId = obstacle.id;
      }
    }
    return { distance: nearest, objectId };
  }

  wallContact(point: Vec2, radius: number, projection?: TrackProjection): WorldContact | null {
    if (this.isOpenJunction(point)) return null;
    projection ??= this.project(point);
    const limit = Math.max(0, projection.allowedHalfWidth - radius);
    const penetration = Math.abs(projection.signedLateralOffset) - limit;
    if (penetration <= 0) return null;
    const heading = this.pointAt(projection.progress).heading;
    const leftNormal: Vec2 = [-Math.sin(heading), Math.cos(heading)];
    const side = projection.signedLateralOffset >= 0 ? 1 : -1;
    return {
      normal: scale(leftNormal, -side),
      penetration,
      contactPoint: add(projection.point, scale(leftNormal, side * projection.allowedHalfWidth)),
    };
  }

  physicalContacts(point: Vec2, radius: number, clearanceZ = 0, projection?: TrackProjection): WorldContact[] {
    const contacts: WorldContact[] = [];
    const wall = this.wallContact(point, radius, projection);
    if (wall) contacts.push(wall);
    for (const obstacle of this.obstacles) {
      if (clearanceZ > obstacle.clearance_height) continue;
      const contact = circleObstacleContact(obstacle, point, radius);
      if (contact) contacts.push(contact);
    }
    return contacts;
  }

  clearedObstacles(point: Vec2, radius: number, clearanceZ: number): WorldObstacleData[] {
    return this.obstacles.filter(
      (obstacle) => clearanceZ > obstacle.clearance_height && circleObstacleContact(obstacle, point, radius) !== null,
    );
  }

  pointAt(progress: number): { point: Vec2; heading: number } {
    const wrapped = ((progress % this.length) + this.length) % this.length;
    for (let index = 0; index < this.segmentLengths.length; index += 1) {
      const startProgress = this.cumulative[index] ?? 0;
      const segmentLength = this.segmentLengths[index] ?? 0;
      if (wrapped <= startProgress + segmentLength) {
        const [start, end] = this.segments[index] as [Vec2, Vec2];
        const fraction = segmentLength > 0 ? (wrapped - startProgress) / segmentLength : 0;
        return { point: add(start, scale(sub(end, start), fraction)), heading: Math.atan2(end[1] - start[1], end[0] - start[0]) };
      }
    }
    const [start, end] = this.segments.at(-1) as [Vec2, Vec2];
    return { point: cloneVec(end), heading: Math.atan2(end[1] - start[1], end[0] - start[0]) };
  }

  isOpenJunction(point: Vec2, margin = 0): boolean {
    return this.openJunctions.some(([x, y, radius]) => Math.hypot(point[0] - x, point[1] - y) <= radius + margin);
  }

  private segmentInsideOpenJunction(start: Vec2, end: Vec2): boolean {
    const vector = sub(end, start);
    const squared = dot(vector, vector);
    return this.openJunctions.some(([x, y, radius]) => {
      const center: Vec2 = [x, y];
      const fraction = squared <= 1e-12 ? 0 : clip(dot(sub(center, start), vector) / squared, 0, 1);
      const closest = add(start, scale(vector, fraction));
      return length(sub(closest, center)) <= radius;
    });
  }
}
